import { apiFetchAuthed, getAccessToken } from '../apiHelpers';
import config from '../config';
import useUserStore from '../stores/useUserStore';
import type { PresenceUser } from './types';

// The same exit `useApi` wires in: a session that cannot be refreshed signs the user out.
const onUnauthorized = () => useUserStore.getState().logout();

const call = (url: string, options: Record<string, unknown> = {}) => apiFetchAuthed(url, { onUnauthorized, ...options });

const base = (planId: string) => `/node-plans/${encodeURIComponent(planId)}`;

// Silent throughout: unread and presence are hints, and a failure here is not worth a console error.
export const getPresence = (planId: string): Promise<{ presence: PresenceUser[] }> =>
  call(`${base(planId)}/presence`, { silent: true });

export const getUnread = (planId: string): Promise<{ counts: Record<string, number> }> =>
  call(`${base(planId)}/unread`, { silent: true });

export const markNodeSeen = (planId: string, nodeId: string): Promise<void> =>
  call(`${base(planId)}/nodes/${encodeURIComponent(nodeId)}/seen`, { method: 'POST', silent: true }).then(() => undefined);

/**
 * Open the plan's event stream. Resolves with the raw response, whatever its status.
 * fetch rather than EventSource: EventSource cannot send the Bearer header.
 */
export const openPlanEvents = (planId: string, signal: AbortSignal): Promise<Response> => {
  const token = getAccessToken();
  return fetch(`${config.API_ENDPOINT}${base(planId)}/events`, {
    headers: {
      Accept: 'text/event-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: 'include',
    cache: 'no-store',
    signal,
  });
};

/**
 * Read server-sent events off a response body until the server closes it. `onChunk`
 * fires on every read, heartbeat comments included, so a caller can tell the stream is alive.
 */
export async function readEvents(
  res: Response,
  onEvent: (name: string, data: unknown) => void,
  onChunk?: () => void,
): Promise<void> {
  if (!res.body) return;
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let partial = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) return;
    onChunk?.();
    partial += decoder.decode(value, { stream: true }).replace(/\r\n?/g, '\n');
    const frames = partial.split('\n\n');
    partial = frames.pop() || '';
    for (const frame of frames) {
      let name = 'message';
      let data = '';
      for (const line of frame.split('\n')) {
        if (line.startsWith('event:')) name = line.slice(6).trim();
        else if (line.startsWith('data:')) data += line.slice(5).replace(/^ /, '');
      }
      if (!data) continue;
      let parsed: unknown;
      try {
        parsed = JSON.parse(data);
      } catch {
        continue;
      }
      onEvent(name, parsed);
    }
  }
}
